import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import styled from 'styled-components';
import { AiOutlineInfoCircle } from 'react-icons/ai';
import { FaSave, FaTrash } from 'react-icons/fa';
import { GrRevert } from 'react-icons/gr';

import { colors } from 'constants/colors';

import { TotemPrimaryButton } from 'components/TotemPrimaryButton';
import { ColumnLocationSelector } from './ColumnLocationSelector';
import { MicrostoreColumnEditorInfoPopup } from './Popups/MicrostoreColumnEditorInfoPopup';

import {
    MicrostoreColumnGroupFormValues,
    MicrostoreColumnFormValues,
    MicrostoreLocationFormValues,
    MicrostoreProductsStockInfoFormValues,
} from './FormHelper/DataMapper';
import { TotemInput } from 'components/TotemInput';
import { formatDateAsAnniversary } from 'helpers/dateTimes';
import {
    GetMicrostoreSitesWithLocationsInfoQuery,
    GetMicrostoreSiteQuery,
    GetProductsWithRecipesQuery,
    TotadminPermission,
} from 'data/__generated__';
import { BiArrowFromTop } from 'react-icons/bi';

export const ColumnPanel = ({
    canBeUpdated = true,
    earliestDLCDateToKeepForStockCalculation,
    displayOnlyTomorrowForPrint = false,
    hideTodayInfo,
    hideTomorrowInfo,
    isFormDirty,
    isToday,
    microstoreColumnGroupFormValuesArray,
    microstoreProductsStockInfo,
    getMicrostoreProductsStockInfoLoading,
    nextMSDeliveryDate,
    nextSiteDeliveryDate,
    productsWithRecipes,
    revertLastUpdate,
    selectedColumnGroup,
    selectedColumn,
    selectedLocationPosition,
    selectedShelfIndex,
    setIsToday,
    setSelectedLocationPosition,
    setSelectedShelfIndex,
    site,
    submitColumnUpdate,
    switchColumn,
    updateSelectedColumn,
    updateSelectedLocation,
}: {
    canBeUpdated?: boolean;
    earliestDLCDateToKeepForStockCalculation: string | null;
    displayOnlyTomorrowForPrint?: boolean;
    hideTodayInfo?: boolean;
    hideTomorrowInfo?: boolean;
    isFormDirty: boolean;
    isToday: boolean;
    microstoreColumnGroupFormValuesArray: MicrostoreColumnGroupFormValues[];
    microstoreProductsStockInfo: MicrostoreProductsStockInfoFormValues | null;
    getMicrostoreProductsStockInfoLoading?: boolean;
    nextMSDeliveryDate: string;
    nextSiteDeliveryDate: string | null;
    productsWithRecipes: GetProductsWithRecipesQuery['getAllProducts'];
    revertLastUpdate: () => void;
    selectedColumnGroup: MicrostoreColumnGroupFormValues;
    selectedColumn: MicrostoreColumnFormValues;
    selectedLocationPosition: { column: number; row: number } | null;
    selectedShelfIndex: number | null;
    setIsToday: React.Dispatch<React.SetStateAction<boolean>>;
    setSelectedLocationPosition: React.Dispatch<React.SetStateAction<{ column: number; row: number } | null>>;
    setSelectedShelfIndex: React.Dispatch<React.SetStateAction<number | null>>;
    site: GetMicrostoreSiteQuery['siteWithLocationInfo'] | GetMicrostoreSitesWithLocationsInfoQuery['sites'][number];
    submitColumnUpdate: () => void;
    switchColumn: MicrostoreColumnFormValues | null;
    updateSelectedColumn: (values: Partial<MicrostoreColumnFormValues>) => void;
    updateSelectedLocation: (values: Partial<MicrostoreLocationFormValues>) => void;
}) => {
    const [isMicrostoreColumnEditorInfoPopup, setIsMicrostoreColumnEditorInfoPopup] = useState<boolean>(false);

    const todayDate = formatDateAsAnniversary({ dateTime: new Date() });

    function autoFillJ() {
        if (!switchColumn) {
            return;
        }

        const newShelves = selectedColumn.shelves.map((shelf, shelfIndex) => {
            const swithShelf = switchColumn.shelves?.[shelfIndex];

            if (!swithShelf) {
                return shelf;
            }

            return {
                ...shelf,
                locationTable: shelf.locationTable.map((locationRow, locationRowIndex) =>
                    locationRow.map((location, locationColIndex) => {
                        const switchLocation = swithShelf.locationTable?.[locationRowIndex]?.[locationColIndex];
                        if (!switchLocation) {
                            return location;
                        }
                        return { ...location, productId: switchLocation.productId };
                    }),
                ),
            };
        });

        updateSelectedColumn({ ...selectedColumn, shelves: newShelves });
    }

    function autoFillJ1() {
        if (!selectedColumn) return;

        const newColumn = {
            ...selectedColumn,
            shelves: selectedColumn.shelves.map((shelf) => ({
                ...shelf,
                locationTable: shelf.locationTable.map((row) =>
                    row.map((location) => ({
                        ...location,
                        replacementProductId:
                            location.replacementProductId === null ? location.productId : location.replacementProductId,
                        stockToAdd: location.replacementProductId === null ? 0 : location.stockToAdd,
                    })),
                ),
            })),
        };

        updateSelectedColumn(newColumn);
    }

    function removeAllProductsJ1() {
        if (!selectedColumn) return;

        const newColumn = {
            ...selectedColumn,
            shelves: selectedColumn.shelves.map((shelf) => ({
                ...shelf,
                locationTable: shelf.locationTable.map((row) =>
                    row.map((location) => ({
                        ...location,
                        replacementProductId: null,
                        stockToAdd: 0,
                    })),
                ),
            })),
        };

        updateSelectedColumn(newColumn);
    }

    return (
        <Container>
            <TitleContainer>
                <Title>
                    {selectedColumnGroup.name} - C{selectedColumn.positionInColumnGroup + 1} -{' '}
                    <TitleColumnName $displayForPrint={false}>
                        <TotemInput
                            className="w-[300px]"
                            placeholder="3e étage, cuisine etc..."
                            value={selectedColumn.name}
                            disabled={!canBeUpdated}
                            onChange={(value) => updateSelectedColumn({ ...selectedColumn, name: value })}
                        />
                    </TitleColumnName>
                    <TitleColumnName $displayForPrint>{selectedColumn.name}</TitleColumnName>
                </Title>
                <SubTitle>({site.name})</SubTitle>
                <InfoIcon size={22} onClick={() => setIsMicrostoreColumnEditorInfoPopup(true)} />
                <ButtonLink to={`/microstore-column/${selectedColumn._id}`}>
                    <TotemPrimaryButton
                        $isSecondaryStyle
                        requiredPermissions={[TotadminPermission.MicrostoreColumnsRead]}
                    >
                        Voir Colonne
                    </TotemPrimaryButton>
                </ButtonLink>
            </TitleContainer>
            <ButtonsContainer>
                {canBeUpdated ? (
                    <Button
                        type="button"
                        title="Revert modification"
                        onClick={revertLastUpdate}
                        disabled={!isFormDirty}
                    >
                        <GrRevert size={13} />
                    </Button>
                ) : null}
                {canBeUpdated && switchColumn ? (
                    <Button type="button" title="Auto fill J" onClick={autoFillJ} disabled={isFormDirty}>
                        Auto fill J
                    </Button>
                ) : null}
                <InfoContainer>
                    <DateText>
                        Prochaine date de livraison générale/ de ce store : {nextMSDeliveryDate}/{' '}
                        {nextSiteDeliveryDate ?? 'NA'}
                    </DateText>
                    <DateText>
                        Date de DLC à garder la plus proche : {earliestDLCDateToKeepForStockCalculation ?? 'NA'}
                    </DateText>
                </InfoContainer>
                <DateText
                    $highlight={
                        !!selectedColumn.startingDate &&
                        selectedColumn.startingDate > todayDate &&
                        selectedColumn.startingDate <= nextMSDeliveryDate
                    }
                >
                    Début : {selectedColumn.startingDate ?? 'NA'}
                </DateText>
                <DateText $highlight={!!selectedColumn.removalDate && selectedColumn.removalDate <= nextMSDeliveryDate}>
                    Retrait : {selectedColumn.removalDate ?? 'NA'}
                </DateText>
                {selectedColumn.isPaused ? <DateText $highlight>Colonne mise en pause</DateText> : null}
                <Button type="button" title="Retirer tous les produits J+1" onClick={removeAllProductsJ1}>
                    <FaTrash size={13} />
                </Button>
                <Button type="button" title="Garder tous les produits de J à J+1" onClick={autoFillJ1}>
                    <BiArrowFromTop size={13} />
                </Button>
                {canBeUpdated ? (
                    <Button
                        type="button"
                        title="Mettre à jour"
                        onClick={submitColumnUpdate}
                        disabled={!isFormDirty}
                        requiredPermissions={[TotadminPermission.MicrostoreColumnsManage]}
                    >
                        <FaSave size={13} />
                    </Button>
                ) : null}
            </ButtonsContainer>

            <ColumnEditorContainer>
                <ColumnLocationSelector
                    canBeUpdated={canBeUpdated}
                    displayOnlyTomorrowForPrint={displayOnlyTomorrowForPrint}
                    hideTodayInfo={hideTodayInfo}
                    hideTomorrowInfo={hideTomorrowInfo}
                    isToday={isToday}
                    microstoreColumnGroupFormValuesArray={microstoreColumnGroupFormValuesArray}
                    microstoreProductsStockInfo={microstoreProductsStockInfo}
                    getMicrostoreProductsStockInfoLoading={getMicrostoreProductsStockInfoLoading}
                    earliestDLCDateToKeepForStockCalculation={earliestDLCDateToKeepForStockCalculation}
                    nextSiteDeliveryDate={nextSiteDeliveryDate}
                    productsWithRecipes={productsWithRecipes}
                    selectedColumn={selectedColumn}
                    selectedLocationPosition={selectedLocationPosition}
                    selectedShelfIndex={selectedShelfIndex}
                    setIsToday={setIsToday}
                    setSelectedLocationPosition={setSelectedLocationPosition}
                    setSelectedShelfIndex={setSelectedShelfIndex}
                    site={site}
                    switchColumn={switchColumn}
                    updateSelectedLocation={updateSelectedLocation}
                />
            </ColumnEditorContainer>
            <MicrostoreColumnEditorInfoPopup
                isOpen={isMicrostoreColumnEditorInfoPopup}
                setIsOpen={setIsMicrostoreColumnEditorInfoPopup}
            />
        </Container>
    );
};

const Container = styled.div`
    // we compute the height with vh because we want the column to be visible while taking the maximum height
    min-height: calc(100vh - 200px);
    flex-grow: 1;
    display: flex;
    flex-direction: column;

    @media print {
        display: block;
    }
`;

const TitleContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: baseline;
`;

const Title = styled.span`
    font-size: 20px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: inline-flex;
    align-items: center;

    @media print {
        font-size: 16px;
    }
`;

const TitleColumnName = styled.span<{ $displayForPrint: boolean }>`
    display: ${({ $displayForPrint }) => ($displayForPrint ? 'none' : 'initial')};
    z-index: 1;
    @media print {
        display: ${({ $displayForPrint }) => ($displayForPrint ? 'initial' : 'none')};
    }
    padding-right: 6px;
`;

const SubTitle = styled.span`
    margin-left: 15px;
    font-size: 20px;
    color: ${({ theme }) => theme.infoTextColor};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    @media print {
        font-size: 16px;
    }
`;

const InfoIcon = styled(AiOutlineInfoCircle)`
    margin-left: 15px;
    cursor: pointer;
    color: ${colors.blue};
    z-index: 1;

    &:hover {
        transform: scale(1.1);
    }

    @media print {
        display: none;
    }
`;

const ButtonsContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;

    & > :not(:first-child) {
        margin-left: 5px;
    }

    @media print {
        display: none;
    }
`;

const DateText = styled.div<{ $highlight?: boolean }>`
    display: flex;
    flex-grow: 1;
    text-align: center;
    color: ${({ $highlight }) => ($highlight ? colors.red : 'inherit')};
`;

const Button = styled(TotemPrimaryButton)`
    padding: 5px;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const ColumnEditorContainer = styled.div`
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    overflow: auto;

    @media print {
        display: block;
    }
`;

const InfoContainer = styled.div`
    display: flex;
    flex-direction: column;
    margin-right: 5px;
`;

const ButtonLink = styled(Link)`
    margin-left: 20px;
    margin-top: 5px;
`;
